/**
 * @file        pseudo-express.js
 *
 *              A lightweight url->callback routing scheme which uses a syntax very similar to
 *              a subset of ExpressJS v5. This was designed explicitly for interoperation with
 *              the Target connection class and SocketIO, but could - in theory - be used with
 *              any NodeJS http server.
 *
 * @author      Wes Garland, wes@kingsds.network
 * @date        July 2021, Aug 2025
 */
'use strict';

const os          = require('os');
const querystring = require('querystring');

const debugging = require('dcp/debugging').scope('pseudo-express');
const matchAllUsed = Symbol('match-all-used');
var Buffer = globalThis.Buffer; /* see setBufferCtor */

/**
 * Create, in ExpressJS terminology, an "app" or "router" that is bound to the given server.
 *
 * @param {object|undefined}   httpServer      optional; server to bind to
 * @param {string|Symbol}      handledMarker   optional; if using httpServer arg, sets the marker that
 *                                             replaces request.uri to mark the request as handled
 * @constructor
 */
exports.PseudoExpress = function PseudoExpress(httpServer, handledMarker)
{
  /* __routes    is an array of descriptors. Each descriptor is an object which has properties:
   *
   * method:     the Express API method, eg 'post', 'get', 'all', 'use'.  In most cases this matches the
   *             HTTP method of the incoming request.
   * match:      a regexp or string which must match the request uri exactly
   * search:     an object whose keys are get query parameters and whose values are regexps to match
   * callback:   function to run when the uri and method match the descriptor. Each callback is invoked
   *             with arguments res, req, and next. Res and req are instances of PseudoExpressResult and
   *             PseudoExpressRequest respectively, which are hold the HTTP result and request. Before
   *             calling the callback, PE will parse query strings into objects, assemble POST data
   *             chunks, etc. If the next argument is not invoked, no other callback will be invoked for
   *             the request.
   */
  this.__routes = [];

  /**
   * Add a route. Match can become match and search if it is { path, search }. This lets us extend the
   * Express API without a lot of fuss so that we can match routes based on query strings. Needed to
   * match /?EIO=4 (socketio) and / going to different callbacks in the same daemon, eg with
   *
   * app.get({ path: '/', search: { EIO: /^4$/ }}, callback2);
   * app.get('/', callback1);
   *
   * callback1 would only receive the request /?EIO=4 when callback2 invoked next().
   */
  this.__routes.add = (obj) => {
    const descriptor = Object.assign({}, obj);

    if (typeof obj.match === 'object' && !(obj.match instanceof RegExp))
    {
      descriptor.match  = obj.match.path;
      descriptor.search = obj.match.search;
    }

    this.__routes.push(descriptor);
  }

  if (httpServer)
    this.bindServer(httpServer, handledMarker);
}

/**
 * Static method to change the Buffer constructor used by PseudoExpress.
 */
exports.PseudoExpress.setBufferCtor = function PseudoExpress$$setBufferCtor(ctor)
{
  const oldCtor = Buffer;
  Buffer = ctor;
  return oldCtor;
}

/**
 * API to bind an instance to a specific http server. More than one server per instance is supported, just call
 * this function multiple times.
 *
 * The basic strategy is to set up an "early" request listener which gets things going, and a "late" request
 * listener which handles ending anything we handled in the early phase. Any request listeners which were already
 * in place (eg socketio?) will be left sandwiched in the middle. Since the request url is written, a foreign
 * request handler will not try to handle something that PseudoExpress decided to handle, even if there is
 * url path namespace overlap.... provided the foreign request handler looks at request.url....
 *
 * @param {object}      server          a node http or https Server
 * @param {string}      [handledMarker] replaces request.url if the request has been routed successfully.
 */
exports.PseudoExpress.prototype.bindServer = function PseudoExpress$bindServer(server, handledMarker)
{
  const magicCookie = Symbol('magic-cookie');
  if (!handledMarker)
    handledMarker = 'pseudo-express-handled'; /* must not match any URL that anything bound to this httpd will try to handle */

  /* first request handler */
  server.prependListener('request', (httpRequest, httpResponse) => {
    httpRequest[magicCookie] = this.routeRequest(httpRequest, httpResponse, handledMarker);
  });

  /* middle request handlers are other libs bound to this httpd, eg socketio. They need to mark requests
   * they have handled in order to quell the rogue listener warning.
   */

  /* last request handler */
  server.on('request', async (httpRequest, httpResponse) => {
    if (httpRequest.url === handledMarker) /* was handled by a PE route or a PE-aware friend */
    {
      if (!httpRequest[magicCookie])
        throw new Error(`missing magic cookie for ${httpRequest.method} ${httpRequest.url}`);
      const originalUrl = await httpRequest[magicCookie];
      debugging('requests') && console.debug(`pe: processed ${httpRequest.method.padStart(5, ' ')} ${originalUrl}`);
      return;
    }

    setImmediate(() => {
      if (httpResponse.headersSent)
        console.warn('Warning: rogue listener handled request', httpRequest.method, httpRequest.url);
      else
      {
        debugging() && console.debug(`pe: request ${httpRequest.url} => 404`);
        sendHttpError(httpResponse, 404, `${httpRequest.url} not found.`);
      }
    });
  });
}

/**
 * Internal function  to mark a request as handled.
 * @param {object} request         Node HTTP request object
 * @param {string} handledMarker   see PseudoExpress$bindServer
 */
exports.PseudoExpress.prototype.markRequest = function PseudoExpress$markRequest(request, handledMarker)
{
  debugging('requests') && console.debug('pe: marking', request.method.padStart(7, ' '), request.url, 'handled via', handledMarker);
  request.url = handledMarker;
}

/**
 * Route a request to a request handler. We match request.url against previously-registered routes
 * (eg with .get) and run the first handler we find, then we mark the request (mutate request.url)
 * so that no other request handlers (I'm looking at you, socket.io) will want to touch it.
 *
 * If a request handler throws an exception, we handle the request by responding with an HTTP error.
 *
 * *** important *** - httpRequest.url rewriting to handledMarker has to happen synchronously, so that
 *                     other event handlers are aware that PseudoExpress is dealing with the request.
 *
 * @param {object} httpRequest     Node HTTP request object
 * @param {object} httpResponse    Node HTTP response object
 * @param {string} handledMarker   see PseudoExpress$bindServer
 * @returns {Promise} which must be awaited before assuming the request has finished. This promise
 *                    resolves with the original request url.
 */
exports.PseudoExpress.prototype.routeRequest = function PseudoExpress$routeRequest(httpRequest, httpResponse, handledMarker)
{
  var descriptors = [];
  var i, requestSearch;
  const requestUrl = httpRequest.url; /* cache because it can become handledMarker */
  const requestPath = httpRequest.url.replace(/\?.*$/, ''); /* cache because it can become handledMarker */
  const method = httpRequest.method.toLowerCase();

  i = requestUrl.indexOf('?');
  if (i !== -1) /* query string present - decode */
    requestSearch = querystring.parse(requestUrl.slice(i + 1));
  else
    requestSearch = {};

  try /* uncaught in here become 500 + html stack */
  {
    debugging() && console.debug(`pe: routing ${method.padStart(7, ' ')} ${requestPath}`);
    for (i=0; i < this.__routes.length; i++)
    {
      const descriptor = this.__routes[i];
      if (false
          || (descriptor.method !== 'all' && descriptor.method !== method)
          || (descriptor.match !== matchAllUsed && typeof descriptor.match === 'string' && requestPath !== descriptor.match)
          || (descriptor.match !== matchAllUsed && typeof descriptor.match === 'object' && !descriptor.match.test(requestPath)))
      {
        debugging('requests') && !debugging('all') && console.debug(`pe: ${method} ${requestPath} does not match`, descriptor.method, descriptor.match);
        continue;
      }

      if (descriptor.search)
      {
        for (let key in descriptor.search)
          if (!requestSearch.hasOwnProperty(key) && !descriptor.search[key].test(requestSearch[key]))
            continue;
      }

      if (descriptor.match !== matchAllUsed) /* will be marked by an explicit-match */
        this.markRequest(httpRequest, handledMarker);
      descriptors.push(descriptor);
      if (!descriptor.callback)
        throw new Error(`missing callback for ${method} ${requestPath}`);
      debugging() && console.debug('pe:     via', descriptor.callback.name || '(anonymous function)');
    }

    if (httpRequest.url !== handledMarker)
    {
      debugging() && console.debug('pe:  ignore', httpRequest.url);
      return;
    }
  }
  catch(error)
  {
    this.markRequest(httpRequest, handledMarker);
    sendHttpError(httpResponse, 500, `Error routing ${method} ${requestPath}`, error);
    return Promise.resolve(requestPath);
  }

  /* At this point, we have synchronously marked all httpRequest.url matching routes we intend to
   * handle, and memoized the matching descriptors. It is now safe to "go async".
   */
  async function runCallbacks()
  {
    try
    {
      const requestPE  = new PseudoExpressRequest (httpRequest, requestUrl, requestPath);
      const responsePE = new PseudoExpressResponse(httpResponse, requestPE);

      switch(method)
      {
        default:
          throw new Error(`invalid method: ${method}`);
        case 'get':
          requestPE.query = requestSearch;
          break;
        case 'post':
        {
          let resolve, reject, tick;
          const chunks = [];
          const p$ready = new Promise((res, rej) => ([resolve, reject] = [res, rej]));

          requestPE.on('data', chunk => {
            chunks.push(chunk);
            tick = true;
          });
          requestPE.on('end', function pseudoExpress$postEnd() {
            requestPE.body = Buffer.concat(chunks);
            resolve();
          });
          const requestTimer = setTimeout(() => reject(new Error('request timeout'), 600e3));
          const chunkTimer = setInterval(() => {
            if (tick)
              tick = false;
            else
              reject(new Error(`chunk #${chunks.length} timeout`));
          }, 60e3);
          try
          {
            await p$ready;
          }
          finally
          {
            clearTimeout(requestTimer);
            clearInterval(chunkTimer);
          }
        }
        break;
      }

      descriptorLoop: for (let descriptor of descriptors)
      {
        const handlers = [descriptor].concat(descriptor.next);
        handlerLoop: for (let handler of handlers)
        {
          debugging() && console.debug('pe: invoking callback',
                                       handler.callback.name || typeof handler.callback, 'for',
                                       descriptor.method, 'match =', descriptor.match);
          function next()
          {
            next.called = true;
            if (arguments.length !== 0)
              next.arg = arguments[0];
          }
          handler.callback(requestPE, responsePE, next);
          debugging() && console.debug('handler for', requestPE.url, 'called next:', Boolean(next.called),
                                       next.called ? next.arg : '');
          if (next.arg === 'route')
            break handlerLoop;
          if (next.arg instanceof(Error))
            throw next.arg; /* error handling middleware not supported by PseudoExpress */
          if (!next.called && httpResponse.finished) /* callback invoked .end or .send */
            break descriptorLoop;
        }
      }
    }
    catch(error)
    {
      sendHttpError(httpResponse, 500, `Error processing ${method} ${requestPath}`, error);
    }

    return requestPath;
  }

  return runCallbacks();
}

/**
 * @param {RegExp}    match         regular expression that matches request url (pathname + search)
 */
/**
 * Register an interceptor for the HTTP get method
 *
 * @param {string}    match         string that matches request pathname exactly
 * @param {function}  callback      function to run when the route matches
 */
exports.PseudoExpress.prototype.get = function PseudoExpress$get(match, callback, ...next)
{
  debugging('requests') && console.debug(`pe: added GET method intercept ${match.description || match}: ${callback.name}`);
  this.__routes.add({
    method: 'get',
    match,
    callback,
    next,
  });
}

/**
 * Register an interceptor for the HTTP post method
 * @see PseudoExpress$get
 */
exports.PseudoExpress.prototype.post = function PseudoExpress$post(match, callback, ...next)
{
  debugging('requests') && console.debug(`pe: added POST method intercept ${match.description || match}: ${callback.name}`);
  this.__routes.add({
    method: 'post',
    match,
    callback,
    next,
  });
}

/**
 * Register an interceptor for all HTTP methods
 * @see PseudoExpress$get
 */
exports.PseudoExpress.prototype.all = function PseudoExpress$all(match, callback, ...next)
{
  debugging('requests') && console.debug(`pe: added all-method intercept ${match.description || match}: ${callback.name}`);
  this.__routes.add({
    method: 'all',
    match,
    callback,
    next,
  });
}

/**
 * Register a middleware callback.
 *
 * Use is for middleware, not request handling per se, but it goes in the same order of precedence as
 * as get, post, all, etc., when considering routes.
 *
 * @see PseudoExpress$get
 * @param {RegExp|string}   match   string or regexp that matches the url path
 */
exports.PseudoExpress.prototype.use = function PseudoExpress$use(match, callback, ...next)
{
  if (arguments.length === 1) /* overloaded, match is unspecified */
  {
    /* When the 'use' method is used without an explict match argument, we invoke the callback only when
     * the request matches another "route"
     */
    if (arguments[1])
      next.unshift(arguments[1]);
    callback = arguments[0];
    match = matchAllUsed;
  }

  debugging('requests') && console.debug(`pe: added use middleware callback for ${match.description || match}: ${callback.name}`);
  this.__routes.add({
    method: 'all',
    match,
    callback,
    next,
  });
}

/**
 * Send a response which is an HTML error page for an HTTP error.
 *
 * @param {object}            response   node http response object
 * @param {number}            code       HTTP status code
 * @param {string}            text       the text of the error message
 * @param {Error|undefined}   error    an Error object, causes stack display on browser.
 */
function sendHttpError(response, code, text, error)
{
  const corsHeaders = {
    'access-control-allow-origin':  '*',
    'access-control-allow-headers': 'content-type, cache-control',
  };

  response.setHeader('content-type', 'text/html; charset=utf8');
  response.setHeader('cache-control', 'no-cache');
  for (let header in corsHeaders)
    response.setHeader(header, corsHeaders[header]);

  response.writeHead(code);
  response.write(`<!doctype html><html lang="en"><head><title>${code} - ${text}</title></head>\n`);
  response.write(`<body>\n  <h3>${code} - ${text}</h3>\n`);
  if (error)
    response.write(`  <xmp>${error.stack || error.message}</xmp>\n`);
  response.write('</body><!--- via pseudo express ---></html>');
  response.end();
}

/**
 * Class to represent incoming http request
 * param {object} httpRequest  NodeJS request from httpServer
 * @constructor
 */
function PseudoExpressRequest(httpRequest, requestUrl, requestPath)
{
  for (const key in httpRequest)
    this[key] = httpRequest[key];

  this.headers     = httpRequest.headers;
  this.httpRequest = httpRequest;
  this.url         = requestUrl;
  this.originalUrl = requestUrl; /* Express API to allow url rewrites */
  this.path        = requestPath;
  this.hostname    = this.httpRequest.headers.host?.split(':')[0] ?? os.hostname();
  this.on          = httpRequest.on.bind(httpRequest);
  this.off         = httpRequest.on.bind(httpRequest);
}

/**
 * Class to represent outgoing http response
 * @constructor
 */
function PseudoExpressResponse(httpResponse, request)
{
  this.httpResponse = httpResponse;
  this.headers     = {};
  this.request     = request;
  this.headersSent = false;
  this.on          = httpResponse.on.bind(httpResponse);
  this.off         = httpResponse.on.bind(httpResponse);
}

/**
 * @param {object} field        Object containing multiple fields and values
 */
/**
 * Sets the response’s HTTP header field to value. To set multiple fields at once, pass an
 * object as the parameter.
 *
 * @note Headers are collapsed to lower case
 *
 * @param {string} field        Header name
 * @param {string} value        Value of header
 */
PseudoExpressResponse.prototype.set = function PseudoExpressResponse$$set(field, value)
{
  if (typeof field === 'string')  /* single header */
    this.headers[field.toLowerCase()] = value;
  else /* assume Object => multiple headers */
  {
    for (let entry of Object.values(field))
      this.headers[entry.field.toLowerCase()] = entry.value;
  }

  return this;
}

/**
 * Returns the HTTP response header specified by field. The match is case-insensitive.
 *
 * @param {string} field
 * @returns {string}
 */
PseudoExpressResponse.prototype.get = function PseudoExpressResponse$$get(field)
{
  return this.headers[field.toLowerCase()];
}

/**
 * Send HTTP status and headers immediately, without the respsonse body.
 *
 * @note Express does not appear to have an API that does this. It is needed to allow
 *       streaming content.
 * @returns this, to allow chaining
 */
PseudoExpressResponse.prototype.sendHeaders = function PseudoExpressResponse$$sendHeaders()
{
  if (!this.statusCode)
    this.statusCode = 200;

  if (Math.floor(this.statusCode / 100) === 2 && !this.headers['content-type'])
    throw new Error('content-type not set for 2xx response');

  this.httpResponse.writeHead(this.statusCode, this.headers);
  this.headersSent = true;
  return this;
}

/**
 * Sends the HTTP response.
 * The body parameter can be a Buffer object, a String, an object. Plain objects are serialized via
 * JSON.stringify. This method performs many useful tasks for simple non-streaming responses: For
 * example, it automatically assigns the Content-Length HTTP response header field (unless previously
 * defined), except in the case of 304 responses.
 *
 * When the parameter is a Buffer object, the method sets the Content-Type response header field
 * to “application/octet-stream”, unless previously defined.
 *
 * @param {string} body         The body to send as the response.
 * @returns this, to allow chaining
 */
PseudoExpressResponse.prototype.send = function PseudoExpressResponse$$send(body)
{
  const originalBody = body;
  if (body === 'null' || body === '')
    body = Buffer.alloc(0);
  else if (typeof body === 'string')
    body = Buffer.from(body, 'utf-8');
  else if (typeof body === 'object')
  {
    if (!Buffer.isBuffer(body))
      body = Buffer.from(JSON.stringify(body), 'utf-8');
  }
  else
    throw new Error(`unable to send body (${typeof body})`);

  if (this.statusCode !== 304 && this.headers['content-length'] !== undefined)
    this.set('content-length', body.byteLength);
  if (!this.headers['content-type'])
  {
    if (typeof originalBody === 'string')
      this.headers['content-type'] = 'text/html';
    else if (Buffer.isBuffer(originalBody))
      this.headers['content-type'] = 'application/octet-stream';
    else if (typeof originalBody === 'object')
      this.headers['content-type'] = 'application/json';
  }
  if (!this.headersSent)
    this.sendHeaders();

  try
  {
    this.httpResponse.end.call(this.httpResponse, body)
  }
  catch (error)
  {
    /* I don't think this is ever caught. I think it is always emitted as an error event. /wg feb 2026 */
    if (error.code !== 'ERR_STREAM_WRITE_AFTER_END')
      throw error;
  }

  return this;
}

/**
 * Ends the response process. Arguments are passed directly to the response.end() method of http.ServerResponse.
 * See https://nodejs.org/api/http.html#responseenddata-encoding-callback for argument details.
 *
 * Use to quickly end the response without any data. If you need to respond with data, instead
 * use methods such as res.send() and res.json().
 *
 * @returns this, to allow chaining
 */
PseudoExpressResponse.prototype.end = function PseudoExpressResponse$$end()
{
  if (!this.headersSent)
    this.sendHeaders();

  this.httpResponse.end.apply(this.httpResponse, arguments);
  return this;
}

/**
 * Sends a JSON response. This method sends a response (with the correct content-type) that is the
 * parameter converted to a JSON string using JSON.stringify().
 *
 * @param {any} jsval   The JS value to serialize and send as the body of the response
 * @returns this, to allow chaining
 */
PseudoExpressResponse.prototype.json = function PseudeoExpressResponse$$json(jsval)
{
  this.set('content-type', 'application/json');
  this.send(JSON.stringify(jsval));
  return this;
}

PseudoExpressResponse.prototype.text = function PseudeoExpressResponse$$json(text)
{
  this.set('content-type', 'text/plain');
  this.send(text);
  return this;
}

/**
 * Sets the HTTP status for the response. It is a chainable alias of Node’s response.statusCode.
 *
 * @param {number} statusCode
 * @returns this, to allow chaining
 */
PseudoExpressResponse.prototype.status = function PseudoExpressResponse$$status(statusCode)
{
  this.statusCode = statusCode;
  return this;
}

/**
 * Sets the response Location HTTP header to the specified path parameter.
 * A path value of “back” has a special meaning, it refers to the URL specified in the Referer
 * header of the request. If the Referer header was not specified, it refers to “/”.
 *
 * @param {string|URL|DcpURL} location
 * @returns this, to allow chaining
 */
PseudoExpressResponse.prototype.location = function PseudoExpressResponse$$location(location)
{
  if (location === '..')
    location = this.request.url.replace(/\/[^/]*$/, '') || '/';
  if (location === 'back')
    location = this.request.headers['http-referer'] || '/';

  return this.set('location', location).send(`Redirecting to ${location}`);
}

/**
 * Sets the Content-Type HTTP header to the MIME type as determined by the specified type.
 *
 * If the type starts with 'text/' and the value does not contain a charset attribute, the attribute
 * charset=utf-8 will be added to the value.
 *
 * @param {string} contentType
 * @returns this, to allow chaining
 */
PseudoExpressResponse.prototype.type = function PseudoExpressResponse$$type(contentType)
{
  if (contentType.startsWith('text/') && contentType.includes('charset='))
    contentType += '; charset="utf-8"';
  return this.set('content-type', contentType);
}

/**
 * Redirects to the URL derived from the specified path, with specified status, a positive integer
 * that corresponds to an HTTP status code. If not specified, status defaults to 302.
 *
 * @param {number} [status]
 * @param {string|URL} [path]
 * @returns this, to allow chaining
 */
PseudoExpressResponse.prototype.redirect = function PseudoExpressResponse$$redirect(status, path)
{
  if (typeof status !== 'number')
  {
    path = status;
    status = 302;
  }

  return this.status(status).location(path);
}
